import { useState } from "react";
import { VetoLogo } from "./VetoLogo";

const PLAID_PRIVACY_URL = "https://plaid.com/legal/#end-user-privacy-policy";

type Props = {
  onContinue: () => void;
  onSkip: () => void;
  continueLabel?: string;
  continueDisabled?: boolean;
  continueTestId?: string;
};

/** CertifID/Pinpoint-style co-brand screen before Plaid Link opens. */
export function PlaidIntroInterstitial({
  onContinue,
  onSkip,
  continueLabel = "Continue",
  continueDisabled = false,
  continueTestId = "plaid-intro-continue"
}: Props) {
  const [consent, setConsent] = useState(false);

  return (
    <section className="panel" data-testid="plaid-intro">
      <p className="label">Connect your bank</p>
      <div className="mt-3 flex flex-wrap items-center gap-3" data-testid="plaid-cobrand">
        <VetoLogo height={26} />
        <span className="text-lg font-bold text-[#596158]" aria-hidden>
          ×
        </span>
        <span
          className="rounded-md bg-[#111111] px-3 py-1.5 text-sm font-bold tracking-tight text-white"
          aria-label="Plaid"
        >
          PLAID
        </span>
      </div>
      <h2 className="mt-4 text-xl font-bold">Veto uses Plaid to connect your account</h2>
      <p className="mt-2 text-sm text-[#596158]">
        Link the account you will pay from. Plaid verifies bank details in seconds. Veto records source-account
        evidence for your title office — it does not move money or approve your payment.
      </p>
      <ul className="mt-3 list-disc space-y-1 pl-5 text-sm text-[#596158]">
        <li>Connect in seconds with your bank login</li>
        <li>Encrypted connection; credentials are not stored by Veto</li>
        <li>Evidence for office review only — not a funds guarantee</li>
      </ul>
      <label className="mt-4 flex items-start gap-3 rounded-md border border-[#d8d2c4] bg-[#fffdf8] p-3">
        <input
          data-testid="plaid-privacy-consent"
          type="checkbox"
          checked={consent}
          onChange={(e) => setConsent(e.target.checked)}
        />
        <span className="text-sm">
          I agree to Plaid&apos;s{" "}
          <a className="font-bold underline" href={PLAID_PRIVACY_URL} target="_blank" rel="noopener noreferrer">
            Privacy Policy
          </a>
          .
        </span>
      </label>
      <div className="mt-4 flex flex-wrap gap-2">
        <button
          className="button-primary"
          type="button"
          data-testid={continueTestId}
          disabled={!consent || continueDisabled}
          onClick={onContinue}
        >
          {continueLabel}
        </button>
        <button className="button-secondary" type="button" data-testid="plaid-skip" onClick={onSkip}>
          Skip bank connection
        </button>
      </div>
    </section>
  );
}
